import { create } from 'zustand'

import { esCaja } from './config'

// Códigos de ayuda.
//
// El código de una pantalla se DERIVA de su ruta en lugar de declararse en
// cada componente: así una pantalla nueva ya tiene su código sin que nadie se
// acuerde de ponerlo, y el nombre del artículo se adivina desde la URL.
//
// Los modales y las pantallas con nombre propio usan un override explícito con
// `useAyudaDe('pos.cobro')`, porque un modal no cambia la ruta.

/**
 * Convierte una ruta en el código del artículo.
 *
 * /laesquina                 → inicio
 * /laesquina/productos       → productos
 * /laesquina/config/usuarios → config.usuarios
 * /  (en la caja)            → pos
 */
export function codigoDeRuta(ruta: string, slug?: string): string {
  let resto = ruta

  // Se saca el slug del local. En la caja no hay slug en la dirección, así
  // que no hay nada que sacar.
  if (slug && resto.startsWith(`/${slug}`)) resto = resto.slice(slug.length + 1)

  const partes = resto.split('/').filter(Boolean)

  // La raíz: en la caja el índice es la pantalla de venta, en la web es el
  // tablero. Son dos artículos distintos porque son dos pantallas distintas.
  if (partes.length === 0) return esCaja() ? 'pos' : 'inicio'

  // La caja web está en /caja pero es la misma pantalla que el POS del
  // mostrador, y comparte su artículo.
  if (partes[0] === 'caja') partes[0] = 'pos'

  return partes.join('.')
}

interface EstadoAyuda {
  abierta: boolean

  /**
   * Overrides apilados.
   *
   * Es una pila y no un solo valor porque los modales se anidan: el diálogo de
   * cobro se abre desde el POS, y al cerrarse la ayuda tiene que volver a la
   * del POS y no quedarse en la del cobro. El último que se registró es el que
   * manda.
   */
  overrides: string[]

  abrir: (codigo?: string) => void
  cerrar: () => void
  alternar: () => void
  registrar: (codigo: string) => void
  quitar: (codigo: string) => void
}

export const useAyuda = create<EstadoAyuda>((set) => ({
  abierta: false,
  overrides: [],

  abrir: (codigo) =>
    set((s) => ({
      abierta: true,
      overrides: codigo ? [...s.overrides, codigo] : s.overrides,
    })),

  cerrar: () => set({ abierta: false }),
  alternar: () => set((s) => ({ abierta: !s.abierta })),

  registrar: (codigo) => set((s) => ({ overrides: [...s.overrides, codigo] })),

  quitar: (codigo) =>
    set((s) => {
      // Se quita la ÚLTIMA aparición y no todas: dos modales del mismo tipo
      // abiertos a la vez es raro pero posible, y quitar todas dejaría la pila
      // vacía con uno todavía abierto.
      const i = s.overrides.lastIndexOf(codigo)
      if (i < 0) return s

      return { overrides: [...s.overrides.slice(0, i), ...s.overrides.slice(i + 1)] }
    }),
}))

/** El código vigente: el último override, o el de la ruta. */
export function codigoVigente(overrides: string[], ruta: string, slug?: string): string {
  return overrides.at(-1) ?? codigoDeRuta(ruta, slug)
}
